<template>
	<footer
		class="webmd-footer"
		data-js="global-footer"
		role="contentinfo"
		tabindex="-1"
		:class="{'overlay': isOverlay, 'no-nls': hideNewsletter || hideNLModule}"
	>
		<i
			v-if="isOverlay"
			@click="hideOverlay"
			class="overlay-close webmd-icon-close"
		>
			<span class="visually-hidden">
				close
			</span>
		</i>
		<div v-if="displayNavigationModule && tocLink && tocLink.url" class="promo-plus-banner">
			<div class="banner-title">
				See More on {{ tocLink.name }} From WebMD
			</div>
			<a :href="`${domain}${tocLink.url}`" v-metric="{module: 'wplusnav'}">
				<button class="banner-btn">
					Go Now
					<i>
						<svg width="25" height="25" viewBox="0 0 25 25" fill="none"
							xmlns="http://www.w3.org/2000/svg">
							<mask id="mask0_9335_18356" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0"
								width="25" height="25">
								<rect x="0.384277" y="0.615234" width="24" height="24" fill="#D9D9D9" />
							</mask>
							<g mask="url(#mask0_9335_18356)">
								<path
									d="M12.9841 12.615L9.08408 8.71504C8.90075 8.53171 8.80908 8.29837 8.80908 8.01504C8.80908 7.73171 8.90075 7.49837 9.08408 7.31504C9.26742 7.13171 9.50075 7.04004 9.78408 7.04004C10.0674 7.04004 10.3007 7.13171 10.4841 7.31504L15.0841 11.915C15.1841 12.015 15.2549 12.1234 15.2966 12.24C15.3382 12.3567 15.3591 12.4817 15.3591 12.615C15.3591 12.7484 15.3382 12.8734 15.2966 12.99C15.2549 13.1067 15.1841 13.215 15.0841 13.315L10.4841 17.915C10.3007 18.0984 10.0674 18.19 9.78408 18.19C9.50075 18.19 9.26742 18.0984 9.08408 17.915C8.90075 17.7317 8.80908 17.4984 8.80908 17.215C8.80908 16.9317 8.90075 16.6984 9.08408 16.515L12.9841 12.615Z"
									fill="#3557FF" />
							</g>
						</svg>
					</i>
				</button>
			</a>
		</div>
		<div
			class="footer-container"
			v-metric="{ module: metrics.module }"
		>
			<link
				rel="dns-prefetch"
				href="https://preferences.trustarc.com"
			/>
			<div v-if="jsLoaded">
				<div
					class="footer-nl-social"
					v-if="!isOverlay"
				>
					<div
						class="toc-nl-mod"
						v-if="!hideNewsletter && !hideNLModule"
					>
						<WebmdNewsletter />
					</div>
					<SocialShare :themed="bgColor" />
				</div>
				<FooterHeaders
					:dis-sublinks="disSublinks"
					:items="items"
					:user-selected="userSelected"
				/>
				<div class="footer-sub-links">
					<FooterSubLinks
						:current-footer-index="currentFooterIndex"
						:metrics="metrics"
						:user-selected-item="userSelectedItem"
						:user-selected="userSelected"
						:show-footer-banner="showFooterBanner"
						:is-ccpa="isCcpa"
					/>
					<FooterCertificates
						:certificate-data="certificateData"
						:interstitial-check="interstitialCheck"
					/>
				</div>
			</div>
			<div
				v-else
				class="footer-noscript-list"
			>
				<ul>
					<li
						v-for="(group, $index) in items.footerLinks"
						:key="$index"
						class="footer-noscript-group"
					>
						<h3>{{ group.group_name }}</h3>
						<ul>
							<li
								v-for="(item, $index2) in group.link_items.link_item"
								:key="$index2"
							>
								<a 
									:href="item.link_friendlyUrl"
									:rel="!item.link_friendlyUrl.includes('webmd.com') ? 'nofollow' : null"
								>
									{{ item.link_text }}
								</a>
							</li>
						</ul>
					</li>
				</ul>
				<FooterCertificates
					:certificate-data="certificateData"
					:interstitial-check="interstitialCheck"
				/>
			</div>
			<div class="global-footer-bottom-container">
				<FooterCopyRight
					:current-year="currentYear"
				/>
				<FooterLogo />
			</div>
		</div>
	</footer>
</template>

<script>
import useStore from 'webmd-cms-store/src/store';

import { MessageBox } from 'webmd-elements';
import _has from 'lodash/has';
import WebmdNewsletter from 'webmd-newsletter/src/Component.vue';
import FooterHeaders from './components/FooterHeaders.vue';
import FooterSubLinks from './components/FooterSubLinks.vue';
import certificateData from './constants/certificates';
import FooterCertificates from './components/FooterCertificates.vue';
import FooterCopyRight from './components/FooterCopyRight.vue';
import FooterLogo from './components/FooterLogo.vue';
import SocialShare from './components/SocialShare.vue';

export default {
	name: 'WebmdGlobalFooter',
	components: {
		WebmdNewsletter,
		SocialShare,
		FooterHeaders,
		FooterSubLinks,
		FooterCertificates,
		FooterCopyRight,
		FooterLogo,
	},
	props: {
		chronicId: {
			type: String,
			required: true,
		},
		bgColor: {
			type: String,
			default: null,
		},
		hideNewsletter: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			certificateData,
			metrics: {
				module: 'ftr',
			},
			domain: '',
			jsLoaded: false,
			userSelected: 'Policies',
			userSelectedItem: 'Policies',
			currentFooterIndex: 1,
			isCcpa: false,
			currentYear: new Date().getFullYear(),

			/*
			 * Object is used to construct the 3 columns for responsive footer.
			 */
			items: {},
			moduleByChronicId: {},
			isOverlay: false,
			hideNLModule: false,
			displayNavigationModule: false,
			tocLink: {},
		};
	},

	created() {
		this.store = useStore(this.$pinia);
		this.moduleByChronicId = this.getModuleByChronicId;
		this.items = this.getItems;
		if(this.store?.pagedata?.omniture?.s_cap_id === '091e9c5e8266cd23') {
			this.hideNLModule = true;
			this.displayNavigationModule = true;
			this.tocLink = this.getTocLink;
		}

		if (this.bgColor) {
			this.store.pagedata.headHTML += `<style>:root{--webmd-footer-bg: ${this.bgColor}; --webmd-footer-ln: #fff }</style>`;
		}

	},

	computed: {
		getItems() {
			if (!this.moduleByChronicId) {
				return [];
			}

			const module = this.moduleByChronicId;
			const footerGroup = module.module_data.groups.group;
			const social = footerGroup[0].link_items.link_item;
			let groups = footerGroup.slice(1, 6);

			// Extracting the link name and metrics value from the link text
			for (let j = 0; j < social.length; j += 1) {
				const text = social[j].link_text.split(' ');
				const linkName = text[0];
				const linkAbbr = text[1].substr(1, 2);

				social[j].link_name = linkName;
				social[j].link_abbr = linkAbbr;
			}

			groups = groups.map((grp) => {
				/* eslint no-param-reassign: ["error", { "props" : false}] */
				grp.link_items.link_item = grp.link_items.link_item.filter(
					(lnk) => lnk && lnk.link_friendlyUrl,
				);
				return grp;
			});

			const items = {
				socialIcons: social,
				footerLinks: groups,
			};
			// eslint-disable-next-line vue/no-side-effects-in-computed-properties
			this.userSelectedItem =	items.footerLinks.length > 0 ? items.footerLinks[0] : {};
			return items;
		},

		getModuleByChronicId() {
			return this.store.cms.getModules(this.chronicId);
		},

		getTocLink() {
			const module = this.store?.cms?.friendlyURLData;

			if (module?.target?.centers?.Health?.friendlyurl) {
				return {
					name: module.target.centers.Health['#text'] ?? '',
					url: module.target.centers.Health.friendlyurl,
				};
			}

			return {};
		},
	},

	methods: {
		disSublinks(seletedItem, currentFooterIndex) {
			this.userSelected = seletedItem.group_name.trim();
			this.userSelectedItem = seletedItem;
			this.currentFooterIndex = currentFooterIndex;
		},

		getDecodedUrl(item) {
			let furl = '';
			let url = '';
			if (_has(item, 'link_friendlyUrl')) {
				url = item.link_friendlyUrl;
			}
			if (url !== '') {
				furl = decodeURIComponent(url).replace(/\/click\?url=/, '');
			}

			return furl;
		},

		addDoNotSellText() {
			let footerContainer = document.querySelectorAll('.footer-sub-links .sub-links');
			let $lnkPlacement;
			const liElement = document.getElementById('ot_footer');
			if (footerContainer.length && footerContainer[0]) {
				// eslint-disable-next-line prefer-destructuring
				footerContainer = footerContainer[0];
				// eslint-disable-next-line prefer-destructuring
				$lnkPlacement = footerContainer.getElementsByTagName('li')[4];
			}
			if ($lnkPlacement !== undefined && liElement !== undefined) {
				$lnkPlacement.parentNode.insertBefore(liElement, $lnkPlacement.nextSibling);
			}
		},

		setDomain() {
			const self = this;
			self.domain = window.location.origin;
		},

		interstitialCheck(event, pValue, sValue) {
			let pindex;
			let subindex;
			if (pValue && sValue && sValue !== 'slink') {
				pindex = pValue + 1;
				subindex = sValue + 1;
			} else if (sValue === 'slink') {
				pindex = pValue;
				subindex = sValue;
			}
			const href =				event.currentTarget.getAttribute('href')
				|| event.target.getAttribute('href')
				|| '';
			let hostname = href.replace(/^(.*\/\/[^/?#]*).*$/, '$1');

			if (hostname.length === 0) {
				return;
			}

			hostname = hostname.substring(hostname.indexOf('//') + 2);

			if (hostname.indexOf('.webmd.com') > -1) {
				window.location.href = href;
			} else {
				const timer = this.interstitialTimeout(href, 15000, pindex, subindex);
				this.interstitialTrigger(href, timer, pindex, subindex);
			}
		},

		interstitialTimeout(href, timer = 5000, pindex, subindex) {
			return setTimeout(() => {
				if (pindex && subindex) {
					if (subindex !== 'slink') {
						this.$vwmdPageLink({
							link: `${this.metrics.module}_${pindex}-${subindex}`,
						});
					} else {
						this.$vwmdPageLink({ link: `${this.metrics.module}_${pindex}` });
					}
				}
				window.location.href = href;
			}, timer);
		},

		interstitialTrigger(url, timer, pindex, subindex) {
			MessageBox.confirm(
				"You are about to visit a website outside of WebMD. Please familiarize yourself with this other website's Privacy Policy as it differs from ours.",
				'Thank you for visiting WebMD',
				{
					confirmButtonText: 'Go',
					cancelButtonText: 'Cancel',
					cancelButtonClass: 'onc-footer-popup-cancel-button',
					size: 'large',
				},
			)
				.then(() => {
					if (pindex && subindex) {
						if (subindex !== 'slink') {
							this.$vwmdPageLink({
								link: `${this.metrics.module}_${pindex}-${subindex}`,
							});
						} else {
							this.$vwmdPageLink({ link: `${this.metrics.module}_${pindex}` });
						}
					}
					window.location = url;
				})
				.catch(() => {
					clearTimeout(timer);
				});
		},

		showFooterBanner() {
			if (typeof window.truste.bn.reopenBanner() !== 'undefined') {
				window.truste.bn.reopenBanner();
			}
		},

		showOverlay() {
			this.isOverlay = true;
			document.body.setAttribute('data-content-masked', true);
		},

		hideOverlay() {
			this.isOverlay = false;
			document.body.removeAttribute('data-content-masked');
		},

	},

	mounted() {
		// add external script powering AdChoices
		const self = this;
		if (window.isCa || window.countryBlocked) {
			self.addDoNotSellText();
		}

		// self.checkCCPA();
		self.setDomain();
		const trustarcScript = document.createElement('script');
		trustarcScript.setAttribute(
			'src',
			'https://preferences.trustarc.com/webservices/js?domain=webmd&type=webmd_popnew&js=responsive',
		);
		trustarcScript.setAttribute('data-e2e', 'external-trustarc');
		trustarcScript.async = true;
		document.body.appendChild(trustarcScript);
		this.jsLoaded = true;
		document.addEventListener('webmd.footer.overlay.open', this.showOverlay);
		document.addEventListener('webmd.footer.overlay.close', this.hideOverlay);
	},
};
</script>
<style lang="scss" src="./styles/_footer.scss"/>
